/**
 * CookieYes category/categories that govern the CleverTap web SDK. CleverTap
 * does both behavioural analytics and marketing messaging; product/compliance
 * decided 'analytics' governs it (same switch as PostHog / Datadog RUM).
 * Change this single constant if that decision is revisited.
 */
export const CLEVERTAP_CONSENT_CATEGORIES = ['analytics'];

export interface ConsentData {
  accepted: string[];
  rejected: string[];
}

export interface ConsentPreferences {
  necessary?: boolean;
  functional?: boolean;
  analytics?: boolean;
  performance?: boolean;
  advertisement?: boolean;
  lastUpdated?: string;
}

export type ConsentChangeCallback = (consentData: ConsentData) => void;

/**
 * ConsentManager handles CookieYes consent integration.
 *
 * CookieYes remains the source of truth. Backend persistence will be
 * added separately via FFH-1172.
 */
export class ConsentManager {
  private userId: string | null = null;
  private onConsentChange?: ConsentChangeCallback;
  private handleConsentUpdate: ((event: CustomEvent) => void) | null = null;
  private isListening = false;

  constructor(userId?: string, onConsentChange?: ConsentChangeCallback) {
    if (userId) {
      this.userId = userId;
    }
    if (onConsentChange) {
      this.onConsentChange = onConsentChange;
    }
  }

  /**
   * Initialize the consent manager with user ID and optional callback.
   * Automatically starts listening for consent events.
   *
   * @param userId - The user's unique identifier
   * @param onConsentChange - Optional callback fired when consent changes
   */
  public initialize(
    userId: string,
    onConsentChange?: ConsentChangeCallback
  ): void {
    this.userId = userId;
    if (onConsentChange) {
      this.onConsentChange = onConsentChange;
    }
    this.startListening();
  }

  /**
   * Start listening to CookieYes consent events and forward them to the
   * onConsentChange callback. No server-side persistence happens here.
   */
  public startListening(): void {
    if (typeof window === 'undefined' || this.isListening) {
      return;
    }

    this.handleConsentUpdate = (event: CustomEvent) => {
      const consentData = event.detail;

      if (consentData && consentData.accepted && consentData.rejected) {
        if (this.onConsentChange) {
          this.onConsentChange(consentData);
        }
      }
    };

    document.addEventListener(
      'cookieyes_consent_update',
      this.handleConsentUpdate
    );
    this.isListening = true;
  }

  /**
   * Stop listening to CookieYes consent events.
   * Removes the event listener and updates internal state.
   */
  public stopListening(): void {
    if (this.handleConsentUpdate && this.isListening) {
      document.removeEventListener(
        'cookieyes_consent_update',
        this.handleConsentUpdate
      );
      this.isListening = false;
    }
  }

  /**
   * Check if the user has given consent for all specified categories.
   *
   * @param categories - Array of consent categories to check (e.g., ['analytics', 'functional'])
   * @returns Promise resolving to true if all categories are consented, false otherwise
   *
   * @example
   * ```typescript
   * const hasAnalytics = await manager.hasConsent(['analytics']);
   * const hasAll = await manager.hasConsent(['analytics', 'functional', 'advertisement']);
   * ```
   */
  public async hasConsent(categories: string[]): Promise<boolean> {
    if (typeof window === 'undefined') return false;

    const { isUserActionCompleted, categories: consentCategories } =
      window?.getCkyConsent?.() ?? {};

    if (!isUserActionCompleted || !consentCategories) return false;

    return categories.every((category) => consentCategories[category]);
  }

  /**
   * Get current consent preferences from CookieYes.
   *
   * @returns ConsentPreferences object with all categories, or null if unavailable
   *
   * @example
   * ```typescript
   * const preferences = manager.getCurrentConsent();
   * if (preferences) {
   *   console.log('Analytics consent:', preferences.analytics);
   *   console.log('Last updated:', preferences.lastUpdated);
   * }
   * ```
   */
  public getCurrentConsent(): ConsentPreferences | null {
    if (typeof window === 'undefined') return null;

    const { isUserActionCompleted } = window?.getCkyConsent?.() ?? {};
    const categories = this.getCurrentConsentCategories();

    if (!isUserActionCompleted || !categories) return null;

    return {
      necessary: categories.necessary ?? false,
      functional: categories.functional ?? false,
      analytics: categories.analytics ?? false,
      performance: categories.performance ?? false,
      advertisement: categories.advertisement ?? false,
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Get current consent categories from CookieYes.
   *
   * @returns Record of category names to boolean values, or null if unavailable
   * @private
   */
  private getCurrentConsentCategories() {
    return window?.getCkyConsent?.()?.categories ?? null;
  }

  /**
   * Set the callback function for consent changes.
   *
   * @param callback - Function to call when consent changes occur
   */
  public setConsentChangeCallback(callback: ConsentChangeCallback): void {
    this.onConsentChange = callback;
  }

  /**
   * Update the user ID and restart listening if currently active.
   *
   * @param userId - New user unique identifier
   */
  public setUserId(userId: string): void {
    const wasListening = this.isListening;

    if (wasListening) {
      this.stopListening();
    }
    this.userId = userId;
    if (wasListening) {
      this.startListening();
    }
  }

  /**
   * Get the current user ID.
   *
   * @returns Current user UID or null if not set
   */
  public getUserId(): string | null {
    return this.userId;
  }

  /**
   * Check if currently listening to consent events.
   *
   * @returns true if actively listening for consent changes, false otherwise
   */
  public isCurrentlyListening(): boolean {
    return this.isListening;
  }

  /**
   * Cleanup method to remove event listeners and reset internal state.
   * Call this when the ConsentManager is no longer needed to prevent memory leaks.
   */
  public destroy(): void {
    this.stopListening();
    this.userId = null;
    this.onConsentChange = undefined;
    this.handleConsentUpdate = null;
  }
}
